import { clearPlaygroundState } from "@go-novus/widget";

import { boundShopDomain } from "#/shared/lib/bound-store";

/**
 * Playground 的 `test:` 身份。会话内容由挂件按 uid 存在 sessionStorage 里，
 * 所以 uid 也存在这里：同一个标签页内换令牌、刷新、来回切页都续得上同一段对话。
 * New test 会撤销旧访客，再创建一段匿名对话。
 * 键里带店铺域名：同一浏览器开着多家店时，各店的会话互不串用。
 */
const pendingResets = new Map<string, string>();
const STORAGE_KEY = "gonovus:playground:uid";

function storageKey(): string {
  const shop = boundShopDomain();
  return shop === null ? STORAGE_KEY : `${STORAGE_KEY}:${shop}`;
}

function storage(): Storage | undefined {
  try {
    return sessionStorage;
  } catch {
    // 隐私模式下存不住 uid，退化成每次开新会话，仍然能试聊。
    return undefined;
  }
}

export function readPlaygroundUid(): string | undefined {
  try {
    return storage()?.getItem(storageKey()) ?? undefined;
  } catch {
    return undefined;
  }
}

export function rememberPlaygroundUid(uid: string): void {
  try {
    storage()?.setItem(storageKey(), uid);
  } catch {
    /* 配额耗尽不影响这次试聊。 */
  }
}

/** New test：先清本机状态并保留服务端撤销意图，失败后可重试。 */
export function forgetPlaygroundUid(uid = readPlaygroundUid()): void {
  if (uid) {
    pendingResets.set(storageKey(), uid);
    const shop = boundShopDomain();
    if (shop) clearPlaygroundState(shop, uid);
    try {
      storage()?.setItem(`${storageKey()}:reset`, uid);
    } catch {
      // 存储不可用时仍由内存保留本次撤销意图。
    }
  }
  try {
    storage()?.removeItem(storageKey());
  } catch {
    // pendingResets 优先于旧 uid，不能因存储失败续回旧授权。
  }
}

export function readPlaygroundResetUid(): string | undefined {
  const pending = pendingResets.get(storageKey());
  if (pending) return pending;
  try {
    return storage()?.getItem(`${storageKey()}:reset`) ?? undefined;
  } catch {
    return undefined;
  }
}
export function completePlaygroundReset(uid: string | undefined): void {
  if (uid && readPlaygroundResetUid() === uid) {
    pendingResets.delete(storageKey());
    try {
      storage()?.removeItem(`${storageKey()}:reset`);
    } catch {
      // 重试已完成的服务端撤销是幂等的。
    }
  }
}
